import { useT } from '../lib/i18n'

type Tone = 'ink' | 'protein' | 'carbs' | 'fat' | 'fiber'

interface BarProps {
  value: number
  target: number
  label: string
  unit?: string
  tone?: Tone
  /** The full width of the track. Defaults to 1.5× target, so the target line sits at two thirds. */
  max?: number
}

const TONE_FILL: Record<Tone, string> = {
  ink: 'bg-ink',
  protein: 'bg-protein',
  carbs: 'bg-carbs',
  fat: 'bg-fat',
  fiber: 'bg-fiber',
}

/**
 * A progress bar with a **visible target line**.
 *
 * The target line is the point of it. A bar that fills to 100 % and stops
 * cannot say "you are 30 g past target", and one that rescales silently as the
 * value grows makes yesterday and today incomparable. So the track is a fixed
 * span (1.5× target by default), the target sits at a marked position on it,
 * and going past is drawn in `--over` — "over target", not "bad" (design
 * system § Palette).
 *
 * `tone` takes the nutrient tokens so `MacroBars` in EPIC-06 is a loop over
 * three of these rather than a new component.
 *
 * SUAP-0103.
 */
export function Bar({ value, target, label, unit, tone = 'ink', max }: BarProps) {
  const { t } = useT()
  const span = max ?? target * 1.5
  const pct = (n: number) => `${Math.min(100, Math.max(0, (n / span) * 100))}%`
  const over = value > target

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-baseline justify-between gap-2 text-sm">
        <span className="truncate text-ink">{label}</span>
        <span className={`shrink-0 tabular-nums ${over ? 'text-over' : 'text-ink-soft'}`}>
          {Math.round(value)}
          {unit === undefined ? '' : ` ${unit}`}
          <span className="text-ink-soft">{` / ${Math.round(target)}`}</span>
        </span>
      </div>

      <div
        role="progressbar"
        aria-label={label}
        aria-valuenow={Math.round(value)}
        aria-valuemin={0}
        aria-valuemax={Math.round(span)}
          aria-valuetext={t('bar.ofTarget', {
          value: `${Math.round(value)}${unit === undefined ? '' : ` ${unit}`}`,
          target: Math.round(target),
        })}
        className="relative h-3 w-full overflow-hidden rounded-chip bg-hairline"
      >
        <div
          className={`absolute inset-y-0 left-0 rounded-chip motion-safe:transition-[width] motion-safe:duration-500 ${
            over ? 'bg-over' : TONE_FILL[tone]
          }`}
          style={{ width: pct(value) }}
        />
        {/* Absolute, inside a relative track — never fixed. */}
        <div
          aria-hidden
          className="absolute inset-y-0 w-0.5 -translate-x-1/2 bg-ink"
          style={{ left: pct(target) }}
        />
      </div>
    </div>
  )
}
